import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useState, useEffect } from 'react';
import QuoteModal from '@/components/QuoteModal';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const Contact = () => {
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch('/api/contact-content-public');
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  if (!content) return null;

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adres',
      content: content.address,
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: content.phone,
      link: `tel:${content.phone.replace(/\s/g, '')}`,
    },
    content.mobile && {
      icon: Phone,
      title: 'GSM',
      content: content.mobile,
      link: `tel:${content.mobile.replace(/\s/g, '')}`,
    },
    {
      icon: Mail,
      title: 'Email',
      content: content.email,
      link: `mailto:${content.email}`,
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      content: (
        <div className="text-gray-600 space-y-1">
          <div>{content.working_hours_weekday || 'Pazartesi - Cuma: 09:00 - 18:00'}</div>
          <div>{content.working_hours_saturday || 'Cumartesi: 10:00 - 13:00'}</div>
          <div className="text-red-600 font-medium">{content.working_hours_sunday || 'Pazar: Kapalı'}</div>
        </div>
      ),
    },
  ].filter(Boolean);

  return (
    <>
      <SEO 
        title="İletişim"
        description="MİGA Yapı iletişim bilgileri. Adres: Merkez Mahallesi Güngören Caddesi No:47 Bağcılar/İstanbul. Telefon: 0212 433 01 48. Ücretsiz keşif ve teklif için bize ulaşın."
        keywords="miga yapı iletişim, inşaat firması telefon, bağcılar inşaat, ücretsiz keşif, teklif al"
      />
      <div data-testid="contact-page" className="min-h-screen pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1
              data-testid="contact-title"
              className="heading-font text-5xl sm:text-6xl font-bold text-[#1e4a7a] mb-6"
            >
              {content.title}
            </h1>
            <div className="w-24 h-1 bg-[#dc2626] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content.subtitle}
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div
                  key={index}
                  data-testid={`contact-card-${index}`}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 text-center border border-gray-100"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#1e4a7a] to-[#2563eb] rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="heading-font text-xl font-semibold text-[#1e4a7a] mb-3">{info.title}</h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      className="text-gray-600 hover:text-[#dc2626] transition-colors"
                    >
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-gray-600">{info.content}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Map */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-16 relative group">
            <iframe
              data-testid="contact-map"
              src={content.map_embed_url}
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="MİGA Yapı Konum"
              className="grayscale-[30%] hover:grayscale-0 transition-all duration-500"
            ></iframe>
            
            {/* Get Directions Overlay */}
            <div className="absolute top-4 right-4 z-10">
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(content.address || 'Merkez Mahallesi Güngören Caddesi No:47 Bağcılar İstanbul')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white hover:bg-gray-50 text-[#1e4a7a] px-6 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transition-all flex items-center gap-2 group-hover:scale-110"
              >
                <MapPin className="h-5 w-5" />
                <span>Yol Tarifi Al</span>
              </a>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-[#1e4a7a] to-[#2563eb] rounded-3xl p-12 text-center text-white">
            <h2 className="heading-font text-4xl sm:text-5xl font-bold mb-6">
              {content.cta_title}
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {content.cta_subtitle}
            </p>
            <Button
              onClick={() => setShowQuoteModal(true)}
              data-testid="contact-quote-button"
              className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-6 text-lg rounded-full font-medium transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              Teklif Al
            </Button>
          </div>
        </div>
      </div>

      <QuoteModal open={showQuoteModal} onOpenChange={setShowQuoteModal} />
    </>
  );
};

export default Contact;